import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import AddressLine from './components/AddressLine'
import clsx from 'clsx'
import { ChevronDownIcon } from '@heroicons/react/outline/'
import AnimateHeight from 'react-animate-height'
import SearchInput from '../SearchInput/SearchInput'

const LocationSelect = ({
    locations,
    selectedLocations = [],
    onSelectLocation,
}) => {
    const [locationResults, setLocationResults] = useState(locations)
    const [isOpen, setIsOpen] = useState(false)
    const [width, setWidth] = useState(null)
    const ref = useRef(null)

    useEffect(() => {
        if (ref?.current && !width && isOpen) {
            setWidth(ref?.current?.clientWidth)
        }
    }, [ref, isOpen])

    const toggleOpen = () => {
        setIsOpen(!isOpen)
    }

    const onSearch = text => {
        if (isEmpty(text)) {
            setLocationResults(locations)
        } else {
            const results = locations.filter(
                location =>
                    location?.label
                        ?.toLowerCase()
                        .includes(text.toLowerCase()) ||
                    location?.address
                        ?.toLowerCase()
                        .includes(text.toLowerCase()),
            )
            setLocationResults(results)
        }
    }

    const changeSelectedLocations = loc => {
        let newSelectedLocations = [...selectedLocations]
        const locationExist = selectedLocations.find(l => l?.id === loc?.id)
        if (locationExist) {
            newSelectedLocations = newSelectedLocations.filter(
                l => l?.id !== loc?.id,
            )
        } else {
            newSelectedLocations.push(loc)
        }
        onSelectLocation(newSelectedLocations)
    }

    const locationIsSelected = location => {
        return selectedLocations.find(l => l?.id === location?.id)
            ? true
            : false
    }

    return (
        <div className="flex flex-col relative w-auto">
            <button
                onClick={toggleOpen}
                className="w-auto h-auto px-4 py-3.5 rounded-xl border border-solid border-grayLines bg-white flex flex-row items-center justify-center"
            >
                <p className="text-textDarkGray text-sm font-normal ">
                    {' '}
                    Location: <b>Selected Locations</b>
                </p>
                <div className="text-xs bg-grayRegular rounded-full w-6 h-6 font-medium text-textDarkerGray ml-2 flex items-center justify-center">
                    {selectedLocations?.length}
                </div>
                <ChevronDownIcon
                    className={clsx(
                        'transition-transform text-textDarkerGray w-4 transform ml-4 ',
                        isOpen ? 'rotate-0' : 'rotate-180',
                    )}
                />
            </button>

            <AnimateHeight
                className="absolute right-0 top-16 z-30"
                id="locationList-panel"
                duration={150}
                height={isOpen ? 'auto' : 0}
            >
                <div
                    ref={ref}
                    style={{ width: width || 'auto' }}
                    className={clsx(
                        'bg-white h-102.25 rounded-xl shadow-lg p-4 border border-gray-200 flex flex-col items-center justify-start',
                        locationResults?.length === 0
                            ? 'min-w-91.5'
                            : 'min-w-max',
                    )}
                >
                    <SearchInput
                        placeholder="Search Location"
                        setValue={onSearch}
                        name="findLocation"
                    />

                    <div
                        className={clsx(
                            'flex flex-col flex-grow items-start justify-start mt-4 max-h-full overflow-auto',
                            locationResults?.length > 6 && 'pr-4',
                        )}
                    >
                        {locationResults?.map(loc => {
                            const locationActive = locationIsSelected(loc)
                            return (
                                <AddressLine
                                    key={loc?.id}
                                    {...loc}
                                    checked={locationActive}
                                    onChange={changeSelectedLocations}
                                />
                            )
                        })}
                    </div>
                </div>
            </AnimateHeight>
        </div>
    )
}

LocationSelect.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
            address: PropTypes.string,
            id: PropTypes.number,
            label: PropTypes.string,
            value: PropTypes.string,
        }),
    ).isRequired,
    selectedLocations: PropTypes.array,
    onSelectLocation: PropTypes.func,
}

export default LocationSelect
